interface HomePageProps {
  onNavigate: (page: 'cwiczenia' | 'skojarzenia' | 'teoria') => void
  onOpenAuth: () => void
  isLoggedIn: boolean
}

export function HomePage({ onNavigate, onOpenAuth, isLoggedIn }: HomePageProps) {
  return (
    <div className="min-h-screen bg-paper text-ink flex flex-col">

      {/* ── HERO ─────────────────────────────────────────────── */}
      <section className="flex-1 flex flex-col items-center justify-center text-center px-6 py-20 relative overflow-hidden">
        {/* Klucz wiolinowy — dekoracja tła */}
        <span
          aria-hidden="true"
          className="pointer-events-none select-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 font-display text-ink/[0.03] leading-none"
          style={{ fontSize: '36rem' }}
        >
          𝄞
        </span>

        <div className="relative z-10 max-w-2xl mx-auto">
          <p className="eyebrow mb-4">Kształcenie słuchu muzycznego</p>

          <h1 className="font-display text-ink leading-none mb-6" style={{ fontSize: 'clamp(3.5rem, 10vw, 7rem)' }}>
            Inter­wały
          </h1>

          <div className="staff-divider max-w-xs mx-auto mb-8 opacity-40" />

          <p className="font-body text-lg text-ink/70 leading-relaxed mb-10 max-w-prose mx-auto">
            Słyszysz dwa dźwięki — jak daleko są od siebie? Tego uczy się ucho.
            Nie teorią, lecz powtarzaniem. Tak jak kiedyś śpiewano z rękopisu.
          </p>

          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              onClick={() => onNavigate('cwiczenia')}
              className="btn-primary px-8 py-3 text-base"
            >
              Zacznij ćwiczyć
            </button>
            {!isLoggedIn && (
              <button
                onClick={onOpenAuth}
                className="btn-secondary px-8 py-3 text-base"
              >
                Zaloguj się
              </button>
            )}
          </div>
        </div>
      </section>

      {/* ── SEPARATOR ────────────────────────────────────────── */}
      <div className="staff-divider max-w-4xl mx-auto w-full px-6 opacity-30" />

      {/* ── NAWIGACJA DO SEKCJI ──────────────────────────────── */}
      <section className="py-14 px-6">
        <div className="max-w-4xl mx-auto grid grid-cols-1 sm:grid-cols-3 gap-4 stagger-children">

          {/* Ćwiczenia — karta główna, nieco większa */}
          <button
            onClick={() => onNavigate('cwiczenia')}
            className="manuscript-card-elevated p-7 text-left group col-span-1 sm:row-span-1 transition-all duration-200 hover:border-ochre/40"
          >
            <div className="font-display text-4xl text-ochre leading-none mb-4" aria-hidden="true">♪</div>
            <p className="eyebrow mb-1">Słuch</p>
            <h2 className="font-display text-xl text-ink mb-2 group-hover:text-ochre transition-colors">Ćwiczenia</h2>
            <p className="font-body text-sm text-ink/70 leading-relaxed">
              Usłyszysz dwa dźwięki. Twoje zadanie: nazwać odległość między nimi.
              Powtarzaj, aż odpowiedź przyjdzie sama.
            </p>
          </button>

          {/* Skojarzenia */}
          <button
            onClick={() => onNavigate('skojarzenia')}
            className="manuscript-card-elevated p-6 text-left group transition-all duration-200 hover:border-ochre/40"
          >
            <div className="font-display text-3xl text-ink/65 leading-none mb-4" aria-hidden="true">♩</div>
            <p className="eyebrow mb-1">Pamięć</p>
            <h2 className="font-display text-xl text-ink mb-2 group-hover:text-ochre transition-colors">Skojarzenia</h2>
            <p className="font-body text-sm text-ink/70 leading-relaxed">
              Każdy interwał ma swój utwór-klucz. Ucho zapamiętuje przez melodię
              szybciej niż przez definicję.
            </p>
          </button>

          {/* Teoria */}
          <button
            onClick={() => onNavigate('teoria')}
            className="manuscript-card-elevated p-6 text-left group transition-all duration-200 hover:border-ochre/40"
          >
            <div className="font-display text-3xl text-ink/65 leading-none mb-4" aria-hidden="true">§</div>
            <p className="eyebrow mb-1">Wiedza</p>
            <h2 className="font-display text-xl text-ink mb-2 group-hover:text-ochre transition-colors">Teoria</h2>
            <p className="font-body text-sm text-ink/70 leading-relaxed">
              Budowa interwałów, ich właściwości brzmieniowe i nazwy.
              Do zajrzenia, nie do wkuwania.
            </p>
          </button>

        </div>
      </section>

      {/* ── RĘKOPIS — HISTORIA ───────────────────────────────── */}
      <section className="py-16 px-6 border-t border-ink/10">
        <div className="max-w-4xl mx-auto flex flex-col md:flex-row gap-10 items-center">

          <div className="manuscript-plate shrink-0 w-56 md:w-64">
            <img
              src="/hymn-st-john-guido.jpg"
              alt="Rękopis hymnu Ut queant laxis — chorał z neumami i iluminowanymi inicjałami, ok. XIII w."
              loading="lazy"
            />
          </div>

          <div className="max-w-prose">
            <p className="eyebrow mb-3">U źródeł solmizacji</p>
            <h2 className="font-display text-2xl text-ink mb-4 leading-snug">
              Ut queant laxis — hymn, który dał nam<br/>
              <em>do, re, mi, fa, sol, la</em>
            </h2>
            <p className="font-body text-sm text-ink/70 leading-relaxed mb-3">
              Około 1025 roku Guido z Arezzo — mnich z Toskanii — dostrzegł, że kolejne
              wersety hymnu do św. Jana Chrzciciela zaczynają się od coraz wyższych dźwięków.
              Wziął pierwsze sylaby każdego wersu:
            </p>
            <p className="font-body text-sm leading-relaxed mb-4">
              <span className="text-burgundy font-semibold">Ut</span> queant laxis —{' '}
              <span className="text-burgundy font-semibold">Re</span>sonare fibris —{' '}
              <span className="text-burgundy font-semibold">Mi</span>ra gestorum —{' '}
              <span className="text-burgundy font-semibold">Fa</span>muli tuorum —{' '}
              <span className="text-burgundy font-semibold">Sol</span>ve polluti —{' '}
              <span className="text-burgundy font-semibold">La</span>bii reatum
            </p>
            <p className="font-body text-sm text-ink/70 leading-relaxed">
              Tak narodziła się solmizacja. Każdy interwał, który ćwiczysz w tej aplikacji,
              ma swoją historię sięgającą tego rękopisu.
            </p>
          </div>

        </div>
      </section>

      {/* ── STOPKA ───────────────────────────────────────────── */}
      <footer className="border-t border-ink/10 py-6 px-6">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <span className="font-ui text-xs text-ink/65">
            Interwały v0.3 — kształcenie słuchu muzycznego
          </span>
          <span className="font-ui text-xs text-ink/65">
            𝄞
          </span>
        </div>
      </footer>

    </div>
  )
}
